import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import {
  ArrowRight, ArrowUpRight,
  MessageSquareOff, Star, GitBranchPlus, Globe, Instagram, Linkedin,
  BatteryLow, UserRoundX, PhoneCall, ShieldCheck, Sparkles,
  CheckCircle2, TrendingUp,
} from "lucide-react";
import { Seo } from "@/components/seo/Seo";
import DifferenceGlyph from "@/components/DifferenceGlyph";
import PremiumBackdrop from "@/components/PremiumBackdrop";
import KrishnaLoop from "@/components/KrishnaLoop";

/* ------------------------------------------------------------------ */
/*  /the-difference                                                    */
/*                                                                     */
/*  A silent before/after page. Same business, two systems. The page   */
/*  shows the reader what "below 35" looks like, then what "90+" looks */
/*  like, without saying "we".                                         */
/*                                                                     */
/*  Structure inspired by getwingman.com/landing/wingman/v2 but        */
/*  scoped to Krishna's brief and Champagne aesthetic.                 */
/* ------------------------------------------------------------------ */

const beforeItems = [
  { Icon: PhoneCall, label: "Unattended sales calls", detail: "Rings out. No follow-up." },
  { Icon: Star, label: "Chaotic reviews", detail: "A few stars, mostly silence." },
  { Icon: GitBranchPlus, label: "Broken pipeline", detail: "Leads sit for weeks." },
  { Icon: Globe, label: "Messy website", detail: "Slow, dated, hard to trust." },
  { Icon: Instagram, label: "Silent social", detail: "Posted six weeks ago." },
  { Icon: Linkedin, label: "Dead LinkedIn", detail: "Last post: nine months back." },
  { Icon: BatteryLow, label: "Burnt-out team", detail: "Firefighting every morning." },
  { Icon: UserRoundX, label: "Overloaded founder", detail: "Bottleneck in every call." },
];

const afterItems = [
  { Icon: PhoneCall, label: "Every call answered", detail: "Booked or logged in minutes." },
  { Icon: Star, label: "Organised reviews", detail: "Steady inflow, real replies." },
  { Icon: GitBranchPlus, label: "Clean pipeline", detail: "Every lead nurtured on time." },
  { Icon: Globe, label: "Sharp website", detail: "Loads fast. Converts on its own." },
  { Icon: Instagram, label: "Active social", detail: "Consistent, on-brand, indexed." },
  { Icon: Linkedin, label: "Alive LinkedIn", detail: "Founder posts land weekly." },
  { Icon: MessageSquareOff, label: "Steady enquiry flow", detail: "Warm leads across channels." },
  { Icon: ShieldCheck, label: "Focused founder", detail: "Free to make the real decisions." },
];

/* Animated reputation gauge — counts from `from` to `to` as it enters view */
function ReputationGauge({ from, to, tone = "warn", label, sub }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  const value = useMotionValue(from);
  const display = useTransform(value, (v) => Math.round(v));
  const progress = useTransform(value, (v) => (v / 100) * 360);
  const [rendered, setRendered] = useState(from);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(value, to, { duration: 2.0, ease: [0.16, 1, 0.3, 1] });
    const unsub = display.on("change", (v) => setRendered(v));
    return () => { controls.stop(); unsub(); };
  }, [inView, to, value, display]);

  const stroke = tone === "warn" ? "#8b6f47" : "#C9A961";
  const bg = tone === "warn" ? "rgba(139,111,71,0.15)" : "rgba(201,169,97,0.15)";

  return (
    <div ref={ref} className="flex flex-col items-center">
      <div className="relative h-[180px] w-[180px]">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r="42" fill="none" stroke={bg} strokeWidth="7" />
          <motion.circle
            cx="50" cy="50" r="42" fill="none"
            stroke={stroke} strokeWidth="7" strokeLinecap="round"
            strokeDasharray={`${(rendered / 100) * 263.9} 263.9`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="font-serif text-5xl md:text-6xl leading-none" style={{ color: tone === "warn" ? "#a68566" : "#F5F1E8" }}>
            {rendered}
          </p>
          <p className="mt-2 font-sans text-[10px] uppercase tracking-[0.32em]" style={{ color: tone === "warn" ? "#8b6f47" : "#C9A961" }}>
            / 100
          </p>
        </div>
      </div>
      <p className="mt-6 font-sans text-[11px] uppercase tracking-[0.32em]" style={{ color: tone === "warn" ? "#8b6f47" : "#C9A961" }}>
        {label}
      </p>
      {sub && <p className="mt-2 font-serif italic text-ink/70 text-sm">{sub}</p>}
    </div>
  );
}

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-10% 0px" },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

/* ------------------------------------------------------------------ */
/*  Tile component — used in both BEFORE (desaturated) and AFTER       */
/* ------------------------------------------------------------------ */
function ChaosTile({ Icon, label, detail, i, tone = "before" }) {
  const isBefore = tone === "before";
  return (
    <motion.div
      {...fadeIn(0.04 * i)}
      className="relative rounded-2xl border p-5 backdrop-blur-sm"
      style={{
        borderColor: isBefore ? "rgba(139,111,71,0.35)" : "rgba(201,169,97,0.35)",
        background: isBefore ? "rgba(23,20,17,0.6)" : "rgba(20,26,22,0.55)",
        filter: isBefore ? "grayscale(65%)" : "none",
        opacity: isBefore ? 0.75 : 1,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-9 w-9 flex-none items-center justify-center rounded-lg"
          style={{
            background: isBefore ? "rgba(139,111,71,0.15)" : "rgba(201,169,97,0.12)",
            color: isBefore ? "#8b6f47" : "#C9A961",
          }}
        >
          <Icon className="h-4 w-4" strokeWidth={1.6} />
        </div>
        <div className="min-w-0">
          <p className="font-sans text-[13px] uppercase tracking-[0.2em] text-ink">
            {label}
          </p>
          <p className="mt-1 font-serif italic text-ink/60 text-sm leading-snug">
            {detail}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Wingman-style 4-panel proof section                                */
/* ------------------------------------------------------------------ */
const proofPanels = [
  {
    n: "01",
    title: "Reputation Engine",
    body: "Reviews, Google Business Profile, and citations move from silent to steady. The rating chart begins to climb by itself.",
  },
  {
    n: "02",
    title: "Content Systems",
    body: "Website, LinkedIn, and social publish on schedule. Every post is on-brand, indexed, and drives one predictable action.",
  },
  {
    n: "03",
    title: "Sales Operations",
    body: "Enquiries route, follow-up runs on time, and the founder stops being the bottleneck in the pipeline.",
  },
  {
    n: "04",
    title: "Founder & Team Freedom",
    body: "Meetings shrink. Firefighting stops. The founder returns to product, service, and vision. The team returns to the work they were hired to do.",
  },
];

const faq = [
  {
    q: "How long until my numbers actually move?",
    a: "The reputation and visibility layers begin shifting inside 30 days. A durable move from below 35 to above 90 typically takes 90 to 180 days of steady, structured work.",
  },
  {
    q: "Do you touch every layer or only some?",
    a: "Every layer that shows on this page. Reviews, GBP, social, LinkedIn, website, pipeline, and the founder's own operating rhythm. We do not leave gaps because the leaks continue through the gap.",
  },
  {
    q: "What if I already have parts of this working?",
    a: "Even better. We audit what is already working, protect it, and connect the rest around it so the whole system compounds instead of competing with itself.",
  },
  {
    q: "What does this cost?",
    a: "Start with a free Operations Diagnosis. If the fit is right, we discuss engagement structure. If it is not the right fit, we say so.",
  },
];

export default function TheDifference() {
  return (
    <div className="min-h-screen bg-bg text-ink relative overflow-hidden" data-testid="the-difference-page">
      <PremiumBackdrop />
      <Seo
        title="The Difference · Get Virtual Growth"
        description="Two versions of the same business. Below 35 reputation on the left. 90+ on the right. Nothing else changed except who is running the operations behind the door."
        canonical="https://getvirtualgrowth.com/the-difference"
      />

      {/* ============================================================ */}
      {/*  HERO                                                         */}
      {/* ============================================================ */}
      <section className="relative overflow-hidden">
        <KrishnaLoop variant="backdrop" />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(1200px 500px at 50% 0%, rgba(201,169,97,0.08), transparent 60%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-20 md:pb-24 text-center">
          <motion.div {...fadeIn(0)} className="flex justify-center">
            <DifferenceGlyph />
          </motion.div>
          <motion.p {...fadeIn(0.06)} className="mt-8 font-sans text-[11px] uppercase tracking-[0.45em] text-brass">
            The Difference
          </motion.p>
          <motion.h1
            {...fadeIn(0.08)}
            className="mt-6 font-serif text-ink leading-[1.02] tracking-[-0.015em] mx-auto max-w-4xl"
            style={{ fontSize: "clamp(2.4rem, 5.6vw, 4.4rem)", fontWeight: 400 }}
            data-testid="td-h1"
          >
            Two versions of your business.
            <br />
            <span className="italic text-brass">Same founder. Different systems.</span>
          </motion.h1>
          <motion.p
            {...fadeIn(0.16)}
            className="mt-8 font-serif italic text-ink/75 mx-auto max-w-2xl leading-relaxed"
            style={{ fontSize: "clamp(1.05rem, 1.6vw, 1.3rem)" }}
          >
            Below 35 on the left. Above 90 on the right. Nothing else changed except who is running the operations behind the door.
          </motion.p>
          <motion.div {...fadeIn(0.24)} className="mt-12">
            <Link
              to="/diagnose"
              data-testid="td-cta-hero"
              className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-brass hover:bg-brass/90 px-6 sm:px-8 py-4 font-sans text-[11px] sm:text-[12px] uppercase tracking-[0.24em] sm:tracking-[0.28em] font-semibold text-bg transition-colors duration-500"
            >
              Begin an Operations Diagnosis
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <p className="mt-4 font-sans text-xs text-ink-muted">
              Free. Structured. Written response within 48 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  BEFORE + AFTER SPLIT                                         */}
      {/* ============================================================ */}
      <section className="relative border-t border-brass/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-start">
            {/* BEFORE */}
            <div data-testid="td-before-column">
              <div className="flex items-center justify-between mb-6">
                <p className="font-sans text-[10px] uppercase tracking-[0.4em]" style={{ color: "#8b6f47" }}>
                  Before · Reputation below 35
                </p>
                <span className="inline-block h-px w-16" style={{ background: "rgba(139,111,71,0.4)" }} />
              </div>
              <h2 className="font-serif leading-tight" style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "#a68566" }}>
                Your business has too many loose ends.
              </h2>
              <div className="mt-8 flex justify-center">
                <ReputationGauge from={0} to={32} tone="warn" label="Reputation" sub="Slow. Silent. Slipping." />
              </div>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {beforeItems.map((it, i) => (
                  <ChaosTile key={it.label} {...it} i={i} tone="before" />
                ))}
              </div>
            </div>

            {/* AFTER */}
            <div data-testid="td-after-column">
              <div className="flex items-center justify-between mb-6">
                <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-brass">
                  After · Reputation 90+
                </p>
                <span className="inline-block h-px w-16 bg-brass/40" />
              </div>
              <h2 className="font-serif leading-tight text-ink" style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}>
                Every layer moves in the same direction.
              </h2>
              <div className="mt-8 flex justify-center">
                <ReputationGauge from={32} to={92} tone="gold" label="Reputation" sub="Compounding, month over month." />
              </div>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {afterItems.map((it, i) => (
                  <ChaosTile key={it.label} {...it} i={i} tone="after" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  CONVERGENCE LINE                                             */}
      {/* ============================================================ */}
      <section className="relative">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
          <motion.div {...fadeIn(0)} className="flex items-center justify-center gap-4 text-brass">
            <span className="inline-block h-px w-16 bg-brass/40" />
            <Sparkles className="h-5 w-5" />
            <span className="inline-block h-px w-16 bg-brass/40" />
          </motion.div>
          <motion.p
            {...fadeIn(0.1)}
            className="mt-8 font-serif italic text-ink/85 leading-relaxed"
            style={{ fontSize: "clamp(1.1rem, 2vw, 1.55rem)" }}
          >
            The gap between 32 and 92 is not talent, effort, or luck. It is the
            operating architecture running behind your service. When the architecture
            changes, the numbers follow, month after month, without drama.
          </motion.p>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  4-PANEL PROOF (Wingman style)                                */}
      {/* ============================================================ */}
      <section className="border-y border-brass/15 bg-black/40" data-testid="td-proof">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <motion.p {...fadeIn(0)} className="font-sans text-[11px] uppercase tracking-[0.42em] text-brass">
            What actually changes
          </motion.p>
          <motion.h2
            {...fadeIn(0.08)}
            className="mt-6 font-serif text-ink leading-[1.1] max-w-3xl"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 400 }}
          >
            The four operators that rebuild the whole system in the background.
          </motion.h2>

          <div className="mt-14 grid md:grid-cols-2 gap-8 md:gap-12">
            {proofPanels.map((p, i) => (
              <motion.div
                key={p.n}
                {...fadeIn(0.05 + i * 0.08)}
                className="rounded-2xl border border-brass/20 bg-surface/40 p-8 md:p-10"
                data-testid={`td-panel-${p.n}`}
              >
                <p className="font-sans text-[11px] tracking-[0.32em] text-brass">{p.n}</p>
                <h3 className="mt-4 font-serif text-ink leading-tight" style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.8rem)", fontWeight: 500 }}>
                  {p.title}
                </h3>
                <p className="mt-4 font-sans text-ink/85 leading-[1.7]" style={{ fontSize: "clamp(0.95rem, 1.1vw, 1.05rem)" }}>
                  {p.body}
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-brass">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="font-sans text-[11px] uppercase tracking-[0.28em]">
                    Rebuilt end to end
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  BIG BEFORE/AFTER STAT ROW                                    */}
      {/* ============================================================ */}
      <section>
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <motion.p {...fadeIn(0)} className="font-sans text-[11px] uppercase tracking-[0.42em] text-brass text-center">
            The delta
          </motion.p>
          <motion.h2
            {...fadeIn(0.08)}
            className="mt-6 font-serif text-ink text-center leading-tight mx-auto max-w-3xl"
            style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", fontWeight: 400 }}
          >
            Six numbers that stop lying to you the moment the operations are rebuilt.
          </motion.h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10">
            {[
              { before: "< 35", after: "90+", label: "Reputation score" },
              { before: "6 wks", after: "24 hrs", label: "Enquiry response" },
              { before: "0-2", after: "12-20", label: "Reviews per month" },
              { before: "1 / mo", after: "3-4 / wk", label: "Founder LinkedIn posts" },
              { before: "Stale", after: "Live", label: "Google Business Profile" },
              { before: "Burnt", after: "Focused", label: "Team energy" },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                {...fadeIn(0.04 * i)}
                className="rounded-2xl border border-brass/20 p-5 sm:p-6 md:p-8"
                data-testid={`td-stat-${i}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-serif text-xl sm:text-2xl md:text-3xl" style={{ color: "#8b6f47" }}>{s.before}</span>
                  <TrendingUp className="h-4 w-4 text-brass shrink-0" />
                  <span className="font-serif text-2xl sm:text-3xl md:text-4xl text-brass italic">{s.after}</span>
                </div>
                <p className="mt-4 font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.24em] sm:tracking-[0.28em] text-ink-muted">
                  {s.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FINAL CTA BANNER                                             */}
      {/* ============================================================ */}
      <section className="relative border-t border-brass/15">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(800px 400px at 50% 50%, rgba(201,169,97,0.12), transparent 70%)",
          }}
        />
        <div className="relative max-w-[900px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <motion.p {...fadeIn(0)} className="font-sans text-[11px] uppercase tracking-[0.42em] text-brass">
            One next move
          </motion.p>
          <motion.h2
            {...fadeIn(0.08)}
            className="mt-6 font-serif text-ink leading-[1.05]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.4rem)", fontWeight: 400 }}
          >
            Do not stay on <span style={{ color: "#8b6f47" }}>thirty-two</span>.
          </motion.h2>
          <motion.p
            {...fadeIn(0.16)}
            className="mt-8 font-serif italic text-ink/75 mx-auto max-w-xl leading-relaxed"
            style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.2rem)" }}
          >
            A short, structured read of where your growth is currently leaking. Written by hand. Nothing sold. No auto reports.
          </motion.p>
          <motion.div {...fadeIn(0.24)} className="mt-12 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
            <Link
              to="/diagnose"
              data-testid="td-cta-primary"
              className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-brass hover:bg-brass/90 px-6 sm:px-8 py-4 font-sans text-[11px] sm:text-[12px] uppercase tracking-[0.24em] sm:tracking-[0.28em] font-semibold text-bg transition-colors duration-500"
            >
              Begin an Operations Diagnosis
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/growth-partnership"
              data-testid="td-cta-secondary"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full border border-brass/40 hover:border-brass px-6 py-3.5 sm:py-3 font-sans text-[11px] uppercase tracking-[0.24em] sm:tracking-[0.28em] text-ink hover:text-brass transition-colors duration-500"
            >
              See the partnership model
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FAQ                                                          */}
      {/* ============================================================ */}
      <section className="border-t border-brass/15">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="td-faq">
          <p className="font-sans text-[11px] uppercase tracking-[0.42em] text-brass">Quick answers</p>
          <h2 className="mt-6 font-serif text-ink leading-tight" style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", fontWeight: 400 }}>
            The questions people ask before they book.
          </h2>
          <div className="mt-10 space-y-4">
            {faq.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-brass/20 bg-surface/30 p-6 md:p-7 open:border-brass/50 transition-colors"
                data-testid={`td-faq-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <span className="font-serif text-lg md:text-xl text-ink leading-snug">{f.q}</span>
                  <span className="mt-1 font-sans text-[11px] uppercase tracking-[0.28em] text-brass group-open:hidden">Open</span>
                  <span className="mt-1 font-sans text-[11px] uppercase tracking-[0.28em] text-forest hidden group-open:inline">Close</span>
                </summary>
                <p className="mt-5 font-sans text-ink/85 leading-[1.75]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
